/**
 * Utility functions for generating and validating referral codes
 */

/**
 * Normalizes a company/user name into a referral code format
 * - Converts to lowercase
 * - Removes spaces and special characters
 * - Keeps only alphanumeric characters
 */
export const normalizeToReferralCode = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
    .replace(/\s+/g, '') // Remove any remaining spaces
    .trim();
};

/**
 * Generates a unique referral code by appending numbers if needed
 */
export const generateUniqueReferralCode = (baseName: string, existingCodes: string[] = []): string => {
  let code = normalizeToReferralCode(baseName);
  
  // If the base code is available, use it
  if (!existingCodes.includes(code)) {
    return code;
  }
  
  // Otherwise, append numbers until we find a unique one
  let counter = 1;
  while (existingCodes.includes(`${code}${counter}`)) {
    counter++;
  }
  
  return `${code}${counter}`;
};

/**
 * Validates a referral code format
 * - Must be alphanumeric only
 * - No spaces or special characters
 * - Minimum 3 characters
 */
export const isValidReferralCodeFormat = (code: string): boolean => {
  if (!code || code.length < 3) {
    return false;
  }
  
  // Check if it contains only alphanumeric characters
  return /^[a-zA-Z0-9]+$/.test(code);
};

/**
 * Sanitizes a referral code input by the user
 */
export const sanitizeReferralCode = (code: string): string => {
  return code
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .trim();
};
